import { GraphQLError } from 'graphql/error'
import { FC, type JSX, useEffect } from 'react'

import { CollectionLayoutFragmentFragment } from '../../../../__generated__/graphql/catalog/graphql'
import { reportClientError } from '../../../../app/_components/chrome/scripts/DataDogRumScript'
import { NotFoundPage } from '../../../../app/_not-found.client'
import { TrackPageView } from '../../../../app/_providers/TrackingProvider.client'
import { isExternallyReportedError } from '../../../../lib/utils'
import { CollectionViewHeroSection } from '../../CollectionViewHeroSection'
import { PageTabSection } from '../../page-sections/PageTabSection'
import { VideoSection } from '../../page-sections/VideoSection'

import { CollectionPageSkeleton } from './CollectionPageSkeleton'
import { CollectionGridSection } from './sections/CollectionGridSection'
import { GenderNavSection } from './sections/GenderNavSection'
import { HeroFullBleedSection } from './sections/HeroFullBleedSection'
import { VideoModalBannerSection } from './sections/VideoModalBannerSection'
import { SeoContent } from './SeoContent'
import { CollectionPageTabSectionWrapper } from './styles'

import type { Section } from '../../../../types/graphql'

export interface CollectionViewProps {
  collectionView?: Pick<CollectionLayoutFragmentFragment, 'name' | 'sections' | 'seoContent'>
  errors: readonly Error[]
  handle: string
  loading: boolean
}

export const CollectionView: FC<CollectionViewProps> = ({
  collectionView,
  errors,
  handle,
  loading,
}) => {
  const didQueryError = errors.length > 0

  useEffect(() => {
    if (!didQueryError) return
    if (errors?.[0] && isExternallyReportedError(errors[0] as GraphQLError)) {
      reportClientError({
        error: errors[0],
        context: {
          scope: 'Collection Page',
          label: 'Externally reported error.',
        },
      })
      return
    }

    reportClientError({
      error: errors?.[0],
      context: {
        scope: 'Collection page',
        collectionHandle: handle,
        label: 'collection query failed',
      },
    })
  }, [didQueryError, errors, handle])

  // Note: the original collection name looks like this: "Name | FIGS"
  const collectionNameBeforePipeline = collectionView?.name?.split(' | ')?.[0]

  if (loading) {
    return <CollectionPageSkeleton />
  }

  if (!collectionView || didQueryError) {
    return <NotFoundPage />
  }

  return (
    <>
      <TrackPageView
        pageViewHandle={collectionNameBeforePipeline ?? collectionView.name}
        pageViewCategory='collection'
      />
      {collectionView && (
        <main>
          {collectionView.sections.map((props, index) =>
            renderSection(props as Section, index, handle)
          )}
          {collectionView.seoContent && <SeoContent content={collectionView.seoContent} />}
        </main>
      )}
    </>
  )
}

const renderSection = (
  props: Section,
  index: number,
  collectionViewHandle: string
): JSX.Element | null => {
  const key = `${props.id}-${index}`
  switch (props.__typename) {
    case 'CollectionGridSection':
      return (
        <CollectionGridSection key={key} {...props} collectionViewHandle={collectionViewHandle} />
      )
    case 'CollectionViewHeroSection':
      return <CollectionViewHeroSection key={key} {...props} isCriticalImage={index === 0} />
    case 'GenderNavSection':
      return <GenderNavSection key={key} {...props} />
    case 'HeroFullBleedSection':
      return <HeroFullBleedSection key={key} {...props} isCriticalImage={index === 0} />
    case 'VideoModalBannerSection':
      return <VideoModalBannerSection key={key} {...props} />
    case 'VideoSection':
      return <VideoSection key={key} {...props} isCriticalVideo={index === 0} />
    case 'PageTabSection':
      return (
        <CollectionPageTabSectionWrapper index={index} key={key}>
          <PageTabSection index={index} {...props} />
        </CollectionPageTabSectionWrapper>
      )
    default:
      return null
  }
}
